import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Alert, Avatar, Button, DatePicker, Empty, Input, Modal, Space, Spin, Tag, Typography, Upload,
} from 'antd';
import {
  LockOutlined, PaperClipOutlined, SendOutlined, UnlockOutlined,
} from '@ant-design/icons';
import Moment from 'moment';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import {
  getAccionesDeSeguimiento,
  postAccionDeSeguimiento,
  postAdjuntoDeSeguimiento,
} from 'api/configuracion/seguimientoDeRegistro';

const { Text } = Typography;

const baseURISeguimientos = '/configuraciones/seguimientos-de-registros/';

const COLORES = {
  EMISOR: { fondo: '#e6f4ff', borde: '#91caff', avatar: '#1677ff' },
  RECEPTOR: { fondo: '#f6ffed', borde: '#b7eb8f', avatar: '#52c41a' },
};

const formatDate = (value) => (value ? Moment(value).format('DD/MM/YYYY') : '');
const formatFechaHora = (value) => (value ? Moment(value).format('DD/MM/YYYY [a las] HH:mm') : '');

const iniciales = (nombre) => (nombre || '?')
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map((p) => p[0].toUpperCase())
  .join('');

// Cada línea con contenido del mensaje (título en gris + texto).
function Linea({ titulo, valor }) {
  if (!valor) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{`${titulo}: `}</Text>
      <Text style={{ whiteSpace: 'pre-wrap' }}>{valor}</Text>
    </div>
  );
}

Linea.propTypes = { titulo: PropTypes.string.isRequired, valor: PropTypes.string };
Linea.defaultProps = { valor: null };

// Aviso del sistema dentro del hilo: queda como registro de cuándo y quién cerró o reabrió la nota.
function Aviso({ aviso }) {
  const cierre = aviso.tipo === 'CIERRE';
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
      <div style={{
        background: '#fafafa',
        border: '1px dashed #d9d9d9',
        borderRadius: 16,
        padding: '6px 16px',
        textAlign: 'center',
        maxWidth: '85%',
      }}
      >
        {cierre ? <LockOutlined /> : <UnlockOutlined />}
        <Text strong style={{ marginLeft: 6 }}>
          {`${aviso.nombre || 'El emisor'} ${cierre ? 'cerró' : 'reabrió'} la nota`}
        </Text>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {`${formatFechaHora(aviso.fecha)}`}
          </Text>
        </div>
        {cierre && aviso.comentario_de_cierre && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`Comentario de cierre: ${aviso.comentario_de_cierre}`}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}

Aviso.propTypes = {
  aviso: PropTypes.shape({
    tipo: PropTypes.string,
    nombre: PropTypes.string,
    fecha: PropTypes.string,
    comentario_de_cierre: PropTypes.string,
  }).isRequired,
};

// Un mensaje del hilo. Los del emisor van a la izquierda y los del receptor a la derecha.
function Mensaje({ mensaje, onDescargar, onVer }) {
  const color = COLORES[mensaje.rol];
  // Los mensajes propios (de quien está viendo la conversación) van a la derecha y los de la
  // otra persona a la izquierda, sea cual sea su rol.
  const derecha = !!mensaje.propio;
  return (
    <div style={{ display: 'flex', justifyContent: derecha ? 'flex-end' : 'flex-start', margin: '10px 0' }}>
      <div style={{
        display: 'flex', flexDirection: derecha ? 'row-reverse' : 'row', gap: 10, maxWidth: '85%',
      }}
      >
        <Avatar style={{ backgroundColor: color.avatar, flexShrink: 0 }}>
          {iniciales(mensaje.nombre)}
        </Avatar>
        <div style={{
          background: color.fondo, border: `1px solid ${color.borde}`, borderRadius: 8, padding: '8px 12px',
        }}
        >
          <Space size={6} wrap style={{ marginBottom: 2 }}>
            <Text strong>{mensaje.nombre || 'Sin nombre'}</Text>
            <Tag color={mensaje.rol === 'EMISOR' ? 'blue' : 'green'} style={{ marginRight: 0 }}>
              {mensaje.rol}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(mensaje.fecha)}</Text>
          </Space>
          {(mensaje.puesto || mensaje.area) && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[mensaje.puesto, mensaje.area].filter(Boolean).join(' · ')}
              </Text>
            </div>
          )}
          <Linea titulo="Se Requiere" valor={mensaje.se_requiere} />
          <Linea titulo="Acción(es) Realizada(s)" valor={mensaje.acciones_realizadas} />
          <Linea titulo="Observaciones" valor={mensaje.observaciones} />
          <Linea titulo="Fecha Compromiso" valor={formatDate(mensaje.fecha_compromiso)} />
          <Linea titulo="Fecha de Atención" valor={formatDate(mensaje.fecha_de_atencion)} />
          {(mensaje.adjuntos || []).length > 0 && (
            <div style={{ marginTop: 6 }}>
              {mensaje.adjuntos.map((a) => (
                <div key={a.id}>
                  <Button
                    type="link"
                    size="small"
                    icon={<PaperClipOutlined />}
                    style={{ padding: 0 }}
                    onClick={() => (onVer ? onVer(a) : onDescargar(a))}
                  >
                    {a.nombre_archivo || a.descripcion}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Mensaje.propTypes = {
  mensaje: PropTypes.shape({
    rol: PropTypes.string,
    propio: PropTypes.bool,
    nombre: PropTypes.string,
    puesto: PropTypes.string,
    area: PropTypes.string,
    fecha: PropTypes.string,
    se_requiere: PropTypes.string,
    acciones_realizadas: PropTypes.string,
    observaciones: PropTypes.string,
    fecha_compromiso: PropTypes.string,
    fecha_de_atencion: PropTypes.string,
    adjuntos: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onDescargar: PropTypes.func.isRequired,
  onVer: PropTypes.func,
};

Mensaje.defaultProps = {
  onVer: null,
};

// Hilo de conversación de una Nota de Seguimiento: el emisor (quien creó la nota) y su
// receptor intercambian mensajes, con sus documentos, hasta que el emisor cierra la nota.
function HiloDeSeguimiento({
  seguimiento, onNotaActualizada, onVerAdjunto, refresco, onCambio,
}) {
  const token = useSelector(({ auth }) => auth.token);
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [fecha, setFecha] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [modalCierre, setModalCierre] = useState(false);
  const [comentarioDeCierre, setComentarioDeCierre] = useState('');
  const finalRef = useRef(null);

  const esEmisor = !!seguimiento?.es_emisor;
  const cerrada = !!seguimiento?.se_cierra_la_nota;
  const seguimientoId = seguimiento?.id;

  const fetchAcciones = async () => {
    if (!seguimientoId) {
      setAcciones([]);
      return;
    }
    setLoading(true);
    setAcciones(await getAccionesDeSeguimiento(seguimientoId));
    setLoading(false);
  };

  useEffect(() => {
    fetchAcciones();
    // eslint-disable-next-line
  }, [seguimientoId, refresco]);

  useEffect(() => {
    finalRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [acciones.length]);

  const descargar = async (adjunto) => {
    try {
      const response = await fetch(adjunto.documento, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('No se pudo obtener el documento');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = adjunto.nombre_archivo || adjunto.descripcion || 'documento';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(err);
    }
  };

  if (!seguimientoId) {
    return (
      <Text type="secondary">
        Guarde la nota de seguimiento para iniciar la conversación.
      </Text>
    );
  }

  // Primer mensaje: la propia nota (lo que pidió el emisor al crearla).
  const mensajes = [
    {
      key: 'nota',
      rol: 'EMISOR',
      propio: esEmisor,
      nombre: seguimiento.emisor_nombre,
      puesto: seguimiento.emisor_puesto,
      area: seguimiento.emisor_area,
      fecha: seguimiento.fecha_de_registro,
      se_requiere: seguimiento.se_requiere,
      observaciones: seguimiento.observaciones,
      fecha_compromiso: seguimiento.fecha_compromiso,
      adjuntos: [],
    },
    ...acciones.map((a) => ({
      key: `a${a.id}`,
      rol: a.rol,
      propio: (a.rol === 'EMISOR') === esEmisor,
      nombre: a.usuario_nombre,
      puesto: a.usuario_puesto,
      area: a.usuario_area,
      fecha: a.fecha_de_captura,
      se_requiere: a.se_requiere,
      acciones_realizadas: a.acciones_realizadas,
      observaciones: a.observaciones,
      fecha_compromiso: a.fecha_compromiso,
      fecha_de_atencion: a.fecha_de_atencion,
      adjuntos: a.adjuntos,
      tipo: a.tipo,
      comentario_de_cierre: a.comentario_de_cierre,
    })),
  ];

  // El receptor es el responsable de seguimiento, con su plaza y área.
  const receptoresUnicos = [{
    nombre: seguimiento.responsable_de_seguimiento_nombre,
    plaza: seguimiento.plaza_descripcion,
    area: seguimiento.area_descripcion,
  }].filter((r) => r.nombre);
  const conPlaza = (nombre, plaza, area) => {
    const detalle = [plaza, area].filter(Boolean).join(' · ');
    return detalle ? `${nombre} (${detalle})` : nombre;
  };

  // El turno lo marca el último mensaje (los avisos de cierre/reapertura no cuentan).
  const ultimo = acciones.filter((a) => a.tipo === 'MENSAJE').slice(-1)[0];
  const turnoDelEmisor = !!ultimo && ultimo.rol === 'RECEPTOR';

  const responder = async () => {
    if (!mensaje.trim()) return;
    try {
      setLoading(true);
      const campoMensaje = esEmisor ? 'se_requiere' : 'acciones_realizadas';
      const values = {
        seguimiento: seguimientoId,
        [campoMensaje]: mensaje.trim(),
        fecha_de_registro: Moment(),
      };
      if (fecha) values[esEmisor ? 'fecha_compromiso' : 'fecha_de_atencion'] = fecha;
      const accion = await postAccionDeSeguimiento(values);
      if (!accion) {
        setLoading(false);
        return;
      }
      // Los documentos viajan ligados a este mensaje.
      await Promise.all(archivos.map((archivo) => postAdjuntoDeSeguimiento({
        seguimiento: seguimientoId,
        accion: accion.id,
        descripcion: archivo.name,
        documento: { file: archivo.originFileObj || archivo },
        estados_globales: 1,
      })));
      setMensaje('');
      setFecha(null);
      setArchivos([]);
      await fetchAcciones();
      onCambio?.();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const cambiarCierre = async (cerrar) => {
    try {
      setLoading(true);
      const payload = { se_cierra_la_nota: cerrar };
      if (cerrar && comentarioDeCierre.trim()) {
        payload.comentario_de_cierre = comentarioDeCierre.trim();
      }
      const res = await API.patch(`${baseURISeguimientos}${seguimientoId}/`, payload);
      onSuccess(res, cerrar ? 'Nota cerrada correctamente.' : 'Nota reabierta correctamente.');
      setModalCierre(false);
      setComentarioDeCierre('');
      onNotaActualizada?.(res.data);
      await fetchAcciones();
      onCambio?.();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  let estado;
  if (cerrada) {
    estado = (
      <Alert
        type="success"
        showIcon
        icon={<LockOutlined />}
        message={`Nota cerrada el ${formatDate(seguimiento.fecha_de_cierre_de_la_nota)} por `
          + `${conPlaza(seguimiento.emisor_nombre || 'el emisor', seguimiento.emisor_puesto, seguimiento.emisor_area)}`}
        description={seguimiento.comentario_de_cierre || null}
      />
    );
  } else if (turnoDelEmisor) {
    estado = (
      <Alert
        type="warning"
        showIcon
        message={`Pendiente de revisión del emisor: ${conPlaza(seguimiento.emisor_nombre || 'emisor', seguimiento.emisor_puesto, seguimiento.emisor_area)}`}
      />
    );
  } else {
    estado = (
      <Alert
        type="info"
        showIcon
        message={`Pendiente de respuesta de: ${receptoresUnicos
          .map((r) => conPlaza(r.nombre, r.plaza, r.area)).join('; ') || 'el receptor'}`}
      />
    );
  }

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <div style={{
        display: 'flex', flexDirection: 'column', height: 'calc(100vh - 345px)', minHeight: 420,
      }}
      >
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">Receptor: </Text>
          {receptoresUnicos.length
            ? receptoresUnicos.map((r) => (
              <Tag
                key={r.nombre}
                color="green"
                style={{
                  whiteSpace: 'normal', height: 'auto', lineHeight: 1.3, marginBottom: 4,
                }}
              >
                <div>{r.nombre}</div>
                {(r.plaza || r.area) && (
                  <div style={{ fontSize: 11, opacity: 0.8 }}>
                    {[r.plaza, r.area].filter(Boolean).join(' · ')}
                  </div>
                )}
              </Tag>
            ))
            : <Text type="secondary">Sin receptor</Text>}
        </div>
        {estado}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: '4px 8px',
          margin: '8px 0',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          background: '#fff',
        }}
        >
          {/* marginTop: 'auto' acomoda los mensajes al fondo, como en un chat */}
          <div style={{ marginTop: 'auto' }}>
            {mensajes.length === 1 && !acciones.length && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aún no hay respuestas" style={{ margin: '8px 0' }} />
            )}
            {mensajes.map((m) => (
              m.tipo && m.tipo !== 'MENSAJE'
                ? <Aviso key={m.key} aviso={m} />
                : <Mensaje key={m.key} mensaje={m} onDescargar={descargar} onVer={onVerAdjunto} />
            ))}
            <div ref={finalRef} />
          </div>
        </div>

        {!cerrada && (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 10 }}>
          <Input.TextArea
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            placeholder={esEmisor ? 'Escribe lo que se requiere...' : 'Escribe las acciones realizadas...'}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
          <Space style={{ marginTop: 8, width: '100%', justifyContent: 'space-between' }} wrap>
            <Space wrap>
              <DatePicker
                value={fecha}
                onChange={setFecha}
                format="DD/MM/YYYY"
                placeholder={esEmisor ? 'Fecha Compromiso' : 'Fecha de Atención'}
              />
              <Upload
                multiple
                fileList={archivos}
                beforeUpload={() => false}
                onChange={({ fileList }) => setArchivos(fileList)}
                accept=".pdf,.xls,.xlsx,.jpg,.jpeg,.png"
              >
                <Button icon={<PaperClipOutlined />}>Adjuntar</Button>
              </Upload>
            </Space>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={responder}
              disabled={!mensaje.trim()}
            >
              Responder
            </Button>
          </Space>
        </div>
        )}

        {esEmisor && (
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          {cerrada ? (
            <Button onClick={() => cambiarCierre(false)}>Reabrir Nota</Button>
          ) : (
            <Button danger icon={<LockOutlined />} onClick={() => setModalCierre(true)}>
              Cerrar Nota
            </Button>
          )}
        </div>
        )}

      </div>

      <Modal
        open={modalCierre}
        title="Cerrar Nota"
        okText="Cerrar Nota"
        cancelText="Cancelar"
        onOk={() => cambiarCierre(true)}
        onCancel={() => setModalCierre(false)}
        confirmLoading={loading}
        destroyOnClose
      >
        <Text>
          Al cerrar la nota ya no se podrán agregar respuestas. Se registrará la fecha de cierre.
        </Text>
        <Input.TextArea
          value={comentarioDeCierre}
          onChange={(e) => setComentarioDeCierre(e.target.value)}
          placeholder="Comentario de Cierre (opcional)"
          autoSize={{ minRows: 2, maxRows: 5 }}
          style={{ marginTop: 10 }}
        />
      </Modal>
    </Spin>
  );
}

HiloDeSeguimiento.propTypes = {
  seguimiento: PropTypes.shape({
    id: PropTypes.number,
    es_emisor: PropTypes.bool,
    se_cierra_la_nota: PropTypes.bool,
    emisor_nombre: PropTypes.string,
    emisor_puesto: PropTypes.string,
    emisor_area: PropTypes.string,
    plaza_descripcion: PropTypes.string,
    area_descripcion: PropTypes.string,
    responsable_de_seguimiento_nombre: PropTypes.string,
    se_requiere: PropTypes.string,
    observaciones: PropTypes.string,
    fecha_de_registro: PropTypes.string,
    fecha_compromiso: PropTypes.string,
    fecha_de_cierre_de_la_nota: PropTypes.string,
    comentario_de_cierre: PropTypes.string,
  }),
  onNotaActualizada: PropTypes.func,
  onVerAdjunto: PropTypes.func,
  refresco: PropTypes.number,
  onCambio: PropTypes.func,
};

HiloDeSeguimiento.defaultProps = {
  seguimiento: null,
  onNotaActualizada: null,
  onVerAdjunto: null,
  refresco: 0,
  onCambio: null,
};

export default HiloDeSeguimiento;
